// features/projects/project-workstation.js — PROJECT WORKSTATION SHELL
// ═══════════════════════════════════════════════════════════
// FORKED from features/workstation.js per Tims architecture rule:
// chat is the main driver of any feature, must be isolated.
// This orchestrator owns the project's 3-panel layout. The chat
// panel is project-specific (ProjectChatPanel + useProjectChat).
// Canvas, journey map, and layer 2 stay shared (they're true
// primitives — fork on demand if project-specific behavior is
// later needed).
//
// Layout (same as Solo's workstation.js):
//   ┌─────────────────────────────────────────────────────────┐
//   │  Top bar: PROJECT · title · file count · [Excavate] · [Exit] │
//   ├──────────────────┬────────────────────┬─────────────────┤
//   │  Project Chat    │  Canvas Panel      │  Journey Map    │
//   │  (50%)           │  (50%)             │  (rail)         │
//   └──────────────────┴────────────────────┴─────────────────┘
// ═══════════════════════════════════════════════════════════

import { useState, useEffect, useRef, useCallback } from 'react';
import ProjectChatPanel from './project-chat-panel';
import WorkstationCanvasPanel from '../workstation-canvas-panel';
import WorkstationJourneyMap from '../workstation-journey-map';
import ProjectExcavate from './project-excavate';
import WorkstationFooter from '../workstation-footer';
import ContentActionMenu from '../../components/content-action-menu';
import { useWorkspaceNodes } from '../../hooks/use-workspace-nodes';
import { useArtifacts } from '../../hooks/use-artifacts';
import { getSpaceType } from '../../lib/space-types';

export default function ProjectWorkstation({
  userId,
  supabase,
  userRecord,
  workspaceSession,   // the shared_spaces row for this workspace
  voiceConfig,        // optional — for voice input/output
  onExit,             // callback: close workstation, return to landing
  onSidebarCollapse,  // callback: set sidebar collapsed state
  // When true (set by the Workstation Think tab), we hide the
  // Park + Exit buttons in the topbar — they don't make sense
  // when this 3-panel surface is embedded inside the new
  // Workstation shell, which has its own back/exit affordances.
  embedded = false,
  // Optional: skip auto-greeting on empty threads.
  skipOpener = false,
  // Optional: React node injected in the topbar (rare).
  topbarSlot = null,
}) {
  const workspaceId = workspaceSession?.id;
  const workspaceGoal = workspaceSession?.workspace_metadata?.goal
    || workspaceSession?.name
    || 'Space';

  // ── Focused node — the selection bridge between all three panels ──
  // When a node is focused (via map click, tile click, or artifact
  // creation), the canvas shows that node's artifact in Focus Mode,
  // the journey map highlights it, and the chat can scroll to the
  // originating message. null = Gallery Mode (tile grid).
  const [focusedNodeId, setFocusedNodeId] = useState(null);

  // Multi-thread support: each project workspace has many chat
  // threads on top of the same canvas + journey map. Default
  // thread = workspace_id. When user clicks "+ New Thread" we set
  // this to the new thread's id. ProjectThreadBar drives both.
  const [activeThreadId, setActiveThreadId] = useState(null);
  // null → default thread (session_id = workspace_id)
  // uuid → switched-into thread (session_id = uuid)
  const effectiveSessionId = activeThreadId || null;
  // The resolved artifact data for the focused node — set by the
  // canvas panel via onFocusedArtifactChange. Passed to chat so the
  // API knows what's on the canvas (for edit-in-place).
  const [focusedArtifactData, setFocusedArtifactData] = useState(null);

  // Broadcast canvas focus changes via Supabase Realtime for journey map
  useEffect(() => {
    if (!focusedNodeId || !workspaceId || !supabase) return;
    const node = (nodes || []).find(n => n.id === focusedNodeId);
    try {
      supabase.channel(`journey:${workspaceId}`).send({
        type: 'broadcast',
        event: 'canvas_focus',
        payload: { nodeId: focusedNodeId, name: node?.title || '' },
      });
    } catch { /* non-critical */ }
  }, [focusedNodeId, workspaceId, supabase]);

  // ── Layer 2 (Excavate drawer) visibility ──
  const [layer2Visible, setLayer2Visible] = useState(false);

  // ── Map expand / collapse ──
  const [mapExpanded, setMapExpanded] = useState(false);

  // ── Background "thinking" pulse ──
  // Flips true when the chat panel kicks off the Haiku classifier (or
  // Lens) after a Shift response. The journey map watches this and
  // pulses its rail border so the user sees the system reflecting in
  // real time. Auto-clears 1.2s after the last thinking start so the
  // pulse never gets stuck on if the classifier callback never fires
  // its end signal.
  const [thinking, setThinking] = useState(false);
  const thinkingTimerRef = useRef(null);
  function handleThinking(active) {
    if (active) {
      setThinking(true);
      if (thinkingTimerRef.current) clearTimeout(thinkingTimerRef.current);
      thinkingTimerRef.current = setTimeout(() => setThinking(false), 1200);
    } else {
      if (thinkingTimerRef.current) clearTimeout(thinkingTimerRef.current);
      thinkingTimerRef.current = setTimeout(() => setThinking(false), 200);
    }
  }
  useEffect(() => () => { if (thinkingTimerRef.current) clearTimeout(thinkingTimerRef.current); }, []);

  // ── Resize state — chat / canvas / map ratios ──
  // Stored as fractional units in a CSS grid layout. NOT persisted
  // across reloads — every workspace open starts at the canonical
  // defaults so the layout is predictable. Within a session the user
  // can drag the column boundaries; that change lives in component
  // state and is wiped on refresh.
  //
  // Canonical defaults:
  //   - Map collapsed → chat 1, canvas 1            → 50/50
  //   - Map expanded  → chat 2, canvas 2, map 1     → 40/40/20
  //
  // 40/40/20 over 33/33/33 because chat (where you think) and canvas
  // (where you build) deserve more room than the map (peripheral
  // awareness surface — glance at it, don't stare at it). The map
  // only needs to fit the radial cluster + a few legend rows.
  const [chatFr, setChatFr] = useState(1);
  const [canvasFr, setCanvasFr] = useState(1);
  const [mapFr, setMapFr] = useState(1);
  const layer1Ref = useRef(null);
  const dragStateRef = useRef(null);
  // Skip the auto-reset when layout buttons set custom ratios
  const layoutOverrideRef = useRef(false);

  // Reset ratios whenever the journey map toggles. The reset values
  // depend on whether the map is opening or closing — opening goes
  // to 2/2/1 (40/40/20), closing goes to 1/1 (50/50).
  useEffect(() => {
    if (layoutOverrideRef.current) {
      layoutOverrideRef.current = false;
      return;
    }
    if (mapExpanded) {
      setChatFr(2);
      setCanvasFr(2);
      setMapFr(1);
    } else {
      setChatFr(1);
      setCanvasFr(1);
      setMapFr(1);
    }
  }, [mapExpanded]);

  // Resize handle drag handler.
  //   which='chat-canvas' adjusts chatFr / canvasFr ratio
  //   which='canvas-map'  adjusts canvasFr / mapFr ratio (only when map is expanded)
  // Math: pair pixel width = container width minus the column(s) that
  // aren't part of this pair. Drag delta in px → new ratio relative to
  // the original chat+canvas (or canvas+map) sum.
  function startResize(which) {
    return function onMouseDown(e) {
      e.preventDefault();
      const container = layer1Ref.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const HANDLE_PX = 4;
      // Approximate pair width: total minus the other column(s) and handles
      // For chat-canvas with map collapsed: pair = total - 40 (rail) - 4 (handle)
      // For chat-canvas with map expanded: pair = total * (chat+canvas)/(chat+canvas+map) - handles
      // Close enough — we just need a stable reference for the ratio math.
      let pairPx;
      if (which === 'chat-canvas') {
        if (!mapExpanded) {
          pairPx = rect.width - 40 - HANDLE_PX;
        } else {
          const totalFr = chatFr + canvasFr + mapFr;
          pairPx = (rect.width - HANDLE_PX * 2) * ((chatFr + canvasFr) / totalFr);
        }
      } else {
        const totalFr = chatFr + canvasFr + mapFr;
        pairPx = (rect.width - HANDLE_PX * 2) * ((canvasFr + mapFr) / totalFr);
      }

      dragStateRef.current = {
        startX: e.clientX,
        startChat: chatFr,
        startCanvas: canvasFr,
        startMap: mapFr,
        pairPx,
        which,
        latest: { chatFr, canvasFr, mapFr },
      };

      function onMove(ev) {
        const drag = dragStateRef.current;
        if (!drag) return;
        const delta = ev.clientX - drag.startX;

        if (drag.which === 'chat-canvas') {
          const pairFr = drag.startChat + drag.startCanvas;
          const startChatPx = (drag.startChat / pairFr) * drag.pairPx;
          const newChatPx = Math.max(80, Math.min(drag.pairPx - 80, startChatPx + delta));
          const nextChat = (newChatPx / drag.pairPx) * pairFr;
          const nextCanvas = pairFr - nextChat;
          drag.latest = { chatFr: nextChat, canvasFr: nextCanvas, mapFr: drag.startMap };
          setChatFr(nextChat);
          setCanvasFr(nextCanvas);
        } else {
          const pairFr = drag.startCanvas + drag.startMap;
          const startCanvasPx = (drag.startCanvas / pairFr) * drag.pairPx;
          const newCanvasPx = Math.max(80, Math.min(drag.pairPx - 80, startCanvasPx + delta));
          const nextCanvas = (newCanvasPx / drag.pairPx) * pairFr;
          const nextMap = pairFr - nextCanvas;
          drag.latest = { chatFr: drag.startChat, canvasFr: nextCanvas, mapFr: nextMap };
          setCanvasFr(nextCanvas);
          setMapFr(nextMap);
        }
      }

      function onUp() {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        // Drag stays in component state only — not persisted across
        // refreshes per the canonical-defaults rule above.
        dragStateRef.current = null;
      }

      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    };
  }

  // ── Journey map data layer ──
  const {
    nodes,
    loading: nodesLoading,
    fileCount,
    fileLimit,
    filesAtLimit,
    createMomentNode,
    createFileNode,
    deleteNode,
  } = useWorkspaceNodes({
    workspaceId,
    supabase,
    workspaceGoal,
  });

  // ── Artifact save for canvas creation ──
  const { saveArtifact } = useArtifacts({ userId, supabase });

  // Create a blank artifact from the canvas "+ New" menu.
  // Saves a minimal artifact, creates a file node, and auto-focuses it.
  const BLANK_CONTENT = {
    doc: '# Untitled\n\n',
    code: '// New code\n',
    html: '<!DOCTYPE html>\n<html>\n<head>\n  <style>\n    body { font-family: Georgia, serif; background: #0a0a0a; color: #d4d4d4; padding: 40px; }\n  </style>\n</head>\n<body>\n  <h1>Untitled Page</h1>\n</body>\n</html>',
  };
  const BLANK_TYPES = { doc: 'Document', code: 'Code', html: 'HTML' };
  const BLANK_TITLES = { doc: 'New Document', code: 'New Code', html: 'New Page' };

  const handleCreateBlank = useCallback(async (kind) => {
    if (!kind || filesAtLimit) return;
    try {
      const content = BLANK_CONTENT[kind] || '';
      const saved = await saveArtifact(content, BLANK_TYPES[kind] || 'Document', workspaceId);
      if (!saved) return;
      const result = await createFileNode({
        title: BLANK_TITLES[kind] || 'New Artifact',
        artifactId: saved.id,
        fileSource: 'generated',
        chatMessageId: null,
      });
      if (result?.node?.id) {
        setFocusedNodeId(result.node.id);
      }
    } catch (e) {
      console.error('[workstation] create blank failed:', e.message);
    }
  }, [saveArtifact, createFileNode, workspaceId, filesAtLimit]);

  // ── Sidebar auto-collapse on mount, auto-expand on unmount ──
  useEffect(() => {
    onSidebarCollapse?.(true);
    return () => { onSidebarCollapse?.(false); };
  }, [onSidebarCollapse]);

  // ── Lens + Depth cycle triggers on workspace open ──
  // Belt and suspenders: even before any external cron is wired,
  // opening the workspace fires the cycle endpoints. Both endpoints
  // are idempotent — they have their own recency gates and skip if
  // it's not yet time. So this is safe to call on every mount.
  // Once cron is wired (see /api/cron/tick), this becomes a backup
  // path that mostly hits the gate and skips.
  useEffect(() => {
    if (!workspaceId || !supabase) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: { session: authSession } } = await supabase.auth.getSession();
        const headers = {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authSession?.access_token || ''}`,
        };
        // Fire both in parallel — they don't depend on each other
        await Promise.all([
          fetch('/api/workspace/lens-cycle', {
            method: 'POST',
            headers,
            body: JSON.stringify({ workspaceId }),
          }).then(async (r) => {
            if (cancelled || !r.ok) return;
            const data = await r.json();
            if (data?.cycled && data?.findings_created > 0) {
              // New findings landed — bump the Excavate badge
              window.dispatchEvent(new CustomEvent('workstation:new-excavate-item', {
                detail: { type: 'lens', count: data.findings_created },
              }));
            }
          }).catch(e => console.error('[workstation] lens-cycle failed:', e.message)),
          fetch('/api/workspace/depth-cycle', {
            method: 'POST',
            headers,
            body: JSON.stringify({ workspaceId }),
          }).then(async (r) => {
            if (cancelled || !r.ok) return;
            const data = await r.json();
            if (data?.cycled) {
              window.dispatchEvent(new CustomEvent('workstation:new-excavate-item', {
                detail: { type: 'depth' },
              }));
            }
          }).catch(e => console.error('[workstation] depth-cycle failed:', e.message)),
        ]);
      } catch (e) {
        console.error('[workstation] cycles trigger failed:', e.message);
      }
    })();
    return () => { cancelled = true; };
  }, [workspaceId, supabase]);

  // ── Shortcut help modal ──
  const [shortcutHelpOpen, setShortcutHelpOpen] = useState(false);

  // ── Excavate new-item indicator ──
  // Small sage dot on the Excavate button when new lens findings or
  // depth reports have arrived since the user last opened the drawer.
  // The chat panel dispatches workstation:new-excavate-item after the
  // lens evaluator returns a finding; the depth report card flow will
  // dispatch the same event when manual generation succeeds.
  const [excavateNewCount, setExcavateNewCount] = useState(0);
  useEffect(() => {
    function onNewItem() { setExcavateNewCount(c => c + 1); }
    window.addEventListener('workstation:new-excavate-item', onNewItem);
    return () => window.removeEventListener('workstation:new-excavate-item', onNewItem);
  }, []);
  // Clear the count whenever the drawer opens
  useEffect(() => {
    if (layer2Visible && excavateNewCount > 0) setExcavateNewCount(0);
  }, [layer2Visible, excavateNewCount]);


  // ── Keyboard shortcuts ──
  // ⌘1 / ⌘2 / ⌘3 → focus chat / focus canvas / toggle journey map
  // ⌘E → toggle Excavate drawer
  // ⌘/ → show shortcut cheat sheet
  // Skipped when the user is typing in any editable field so digits and
  // letters in their prose stay theirs. Uses custom events for chat /
  // canvas focus so the workstation does not need refs into children.
  useEffect(() => {
    function isTypingTarget(el) {
      if (!el) return false;
      const tag = (el.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
      if (el.isContentEditable) return true;
      return false;
    }
    function onKey(e) {
      const meta = e.metaKey || e.ctrlKey;
      // ⌘/ — help. Allowed even when typing so the user can pull it up
      // mid-thought without losing context.
      if (meta && e.key === '/') {
        e.preventDefault();
        setShortcutHelpOpen(v => !v);
        return;
      }
      // Esc closes the help if it's open, OR clears canvas focus
      if (e.key === 'Escape') {
        if (shortcutHelpOpen) {
          e.preventDefault();
          setShortcutHelpOpen(false);
          return;
        }
        if (focusedNodeId) {
          e.preventDefault();
          setFocusedNodeId(null);
          return;
        }
      }
      if (!meta) return;
      if (isTypingTarget(e.target)) return;
      if (e.key === '1') {
        e.preventDefault();
        window.dispatchEvent(new CustomEvent('workstation:focus-chat'));
      } else if (e.key === '2') {
        e.preventDefault();
        window.dispatchEvent(new CustomEvent('workstation:focus-canvas'));
      } else if (e.key === '3') {
        e.preventDefault();
        setMapExpanded(v => !v);
      } else if (e.key === 'e' || e.key === 'E') {
        e.preventDefault();
        setLayer2Visible(v => !v);
      } else if (e.key === '[' && focusedNodeId) {
        // ⌘[ → previous artifact
        e.preventDefault();
        const fileNodes = nodes.filter(n => n.type === 'file');
        const idx = fileNodes.findIndex(n => n.id === focusedNodeId);
        if (idx > 0) setFocusedNodeId(fileNodes[idx - 1].id);
      } else if (e.key === ']' && focusedNodeId) {
        // ⌘] → next artifact
        e.preventDefault();
        const fileNodes = nodes.filter(n => n.type === 'file');
        const idx = fileNodes.findIndex(n => n.id === focusedNodeId);
        if (idx >= 0 && idx < fileNodes.length - 1) setFocusedNodeId(fileNodes[idx + 1].id);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [shortcutHelpOpen, focusedNodeId, nodes]);

  // ── Node click → focus the node across all panels ──
  // If the node has an artifact, canvas enters Focus Mode. If the node
  // has a chat_message_id, chat scrolls to the originating message.
  // Journey map highlights the focused node.
  function handleNodeClick(node) {
    if (!node) return;
    // File nodes with artifacts → canvas Focus Mode
    if (node.type === 'file' && node.artifact_id) {
      setFocusedNodeId(node.id);
    }
    // Scroll chat to the originating message (any node type)
    if (node.chat_message_id) {
      const el = document.querySelector(`[data-msg-id="${node.chat_message_id}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  // Called by chat panel after Shift creates an artifact — auto-focus it
  function handleArtifactCreated(nodeId) {
    if (nodeId) setFocusedNodeId(nodeId);
  }

  // Clear focus → return to Gallery Mode
  function handleClearFocus() {
    setFocusedNodeId(null);
    setFocusedArtifactData(null);
  }

  return (
    <div style={{
      position: 'relative',
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      background: '#07070a',
      overflow: 'hidden',
    }}>
      {/* ── TOP BAR ───────────────────────────────────── */}
      <div style={{
        height: 42,
        borderBottom: '1px solid #1e1e2a',
        background: '#07070a',
        display: 'flex',
        alignItems: 'center',
        padding: '0 16px',
        gap: 10,
        flexShrink: 0,
      }}>
        {/* Mode label is always PROJECT here — this orchestrator only
            renders for project-backed workspaces. */}
        <span style={{
          fontSize: 11,
          color: '#c8a96e',
          fontFamily: 'Georgia, serif',
          fontWeight: 700,
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
        }}>
          PROJECT
        </span>
        <div style={{
          fontSize: 12,
          color: '#7a9e82',
          fontStyle: 'italic',
          fontFamily: 'Georgia, serif',
        }}>
          {workspaceSession?.name || 'Space'}
        </div>
        {/* Embed-only slot for tab-specific controls — Think
            tab uses this to inline mode pills + thread mgmt. */}
        {topbarSlot && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 14 }}>
            {topbarSlot}
          </div>
        )}
        <div style={{ flex: 1 }} />
        <div style={{
          fontSize: 10,
          color: filesAtLimit ? '#c46050' : fileCount >= 15 ? '#c8a96e' : '#555',
          fontFamily: 'Georgia, serif',
        }}>
          {fileCount} / {fileLimit} files
        </div>

        {/* Layout preset buttons — gold pill style */}
        <div style={{ display: 'flex', gap: 4 }}>
          <button
            onClick={() => {
              layoutOverrideRef.current = true;
              setChatFr(3);
              setCanvasFr(7);
              if (mapExpanded) setMapExpanded(false);
            }}
            title="30/70 — chat narrow, canvas wide"
            style={{
              fontSize: 9, fontFamily: 'Georgia, serif', fontWeight: 600,
              color: '#c8a96e', background: 'rgba(200,169,110,0.08)',
              border: '1px solid rgba(200,169,110,0.25)',
              borderRadius: 20, padding: '3px 10px',
              cursor: 'pointer', whiteSpace: 'nowrap',
              transition: 'background 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(200,169,110,0.15)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(200,169,110,0.08)'; }}
          >
            30 / 70
          </button>
          <button
            onClick={() => {
              layoutOverrideRef.current = true;
              setChatFr(3);
              setCanvasFr(5);
              setMapFr(2);
              if (!mapExpanded) setMapExpanded(true);
            }}
            title="30/50/20 — three panels open"
            style={{
              fontSize: 9, fontFamily: 'Georgia, serif', fontWeight: 600,
              color: '#c8a96e', background: 'rgba(200,169,110,0.08)',
              border: '1px solid rgba(200,169,110,0.25)',
              borderRadius: 20, padding: '3px 10px',
              cursor: 'pointer', whiteSpace: 'nowrap',
              transition: 'background 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(200,169,110,0.15)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(200,169,110,0.08)'; }}
          >
            30 / 50 / 20
          </button>
        </div>

        {/* Share / Export / Route menu — reuses existing ContentActionMenu */}
        <ContentActionMenu
          context="thread"
          contentId={workspaceId}
          contentType="session"
          messages={[]}
          userId={userId}
          supabase={supabase}
          sessionId={workspaceId}
        />
        <button
          onClick={() => setLayer2Visible(v => !v)}
          title={
            excavateNewCount > 0
              ? `Excavate — ${excavateNewCount} new ${excavateNewCount === 1 ? 'observation' : 'observations'}`
              : "Excavate — surface what's underneath (Lens + Depth)"
          }
          style={{
            background: layer2Visible ? 'rgba(155,143,196,0.1)' : 'none',
            border: `1px solid ${layer2Visible ? '#9b8fc4' : '#2a2a2a'}`,
            borderRadius: 5,
            color: layer2Visible ? '#9b8fc4' : '#888',
            fontSize: 10,
            fontFamily: 'Georgia, serif',
            padding: '4px 10px',
            cursor: 'pointer',
            letterSpacing: '0.04em',
            position: 'relative',
          }}
          onMouseEnter={e => {
            if (!layer2Visible) {
              e.currentTarget.style.borderColor = '#9b8fc4';
              e.currentTarget.style.color = '#9b8fc4';
            }
          }}
          onMouseLeave={e => {
            if (!layer2Visible) {
              e.currentTarget.style.borderColor = '#2a2a2a';
              e.currentTarget.style.color = '#888';
            }
          }}
        >
          Excavate
          {excavateNewCount > 0 && !layer2Visible && (
            <span style={{
              position: 'absolute',
              top: -3,
              right: -3,
              minWidth: 14,
              height: 14,
              borderRadius: 7,
              background: '#7a9e82',
              color: '#07070a',
              fontSize: 8,
              fontWeight: 700,
              fontFamily: 'Georgia, serif',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 4px',
              lineHeight: 1,
              boxShadow: '0 0 0 2px #0d0d0d',
              animation: 'wsexcavate-pulse 2s ease-in-out infinite',
            }}>
              {excavateNewCount > 9 ? '9+' : excavateNewCount}
            </span>
          )}
        </button>
        <style jsx global>{`
          @keyframes wsexcavate-pulse {
            0%, 100% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.12); opacity: 0.85; }
          }
        `}</style>
        {/* Exit hidden when embedded (e.g. inside the new
            Workstation shell's Think tab — shell handles nav). */}
        {!embedded && (
          <button
            onClick={onExit}
            title="Exit workstation"
            style={{
              background: 'none',
              border: '1px solid #2a2a2a',
              borderRadius: 5,
              color: '#888',
              fontSize: 10,
              fontFamily: 'Georgia, serif',
              padding: '4px 12px',
              cursor: 'pointer',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = '#c46050'; e.currentTarget.style.color = '#c46050'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#2a2a2a'; e.currentTarget.style.color = '#888'; }}
          >
            Exit
          </button>
        )}
      </div>

      {/* ── LAYER 1 PANELS ─────────────────────────────
          CSS grid with adjustable fr columns. The track template
          changes when the journey map collapses to a 40px rail.
          Both column boundaries are draggable via ResizeHandle. */}
      <div
        ref={layer1Ref}
        style={{
          flex: 1,
          display: 'grid',
          gridTemplateColumns: mapExpanded
            ? `${chatFr}fr 4px ${canvasFr}fr 4px ${mapFr}fr`
            : `${chatFr}fr 4px ${canvasFr}fr 40px`,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <ProjectChatPanel
          userId={userId}
          supabase={supabase}
          userRecord={userRecord}
          workspaceId={workspaceId}
          workspaceSession={workspaceSession}
          workspaceGoal={workspaceGoal}
          voiceConfig={voiceConfig}
          createMomentNode={createMomentNode}
          createFileNode={createFileNode}
          filesAtLimit={filesAtLimit}
          onThinking={handleThinking}
          onArtifactCreated={handleArtifactCreated}
          focusedArtifact={focusedArtifactData}
          chatSessionIdOverride={effectiveSessionId}
          onSwitchProjectThread={(threadId) => {
            // threadId === workspaceId → revert to default thread
            // (effectiveSessionId becomes null → session_id falls back to workspaceId)
            setActiveThreadId(threadId === workspaceId ? null : threadId);
          }}
          skipOpener={skipOpener}
        />
        <ResizeHandle onMouseDown={startResize('chat-canvas')} />
        <WorkstationCanvasPanel
          workspaceGoal={workspaceGoal}
          workspaceId={workspaceId}
          fileCount={fileCount}
          fileLimit={fileLimit}
          fileNodes={nodes.filter(n => n.type === 'file')}
          focusedNodeId={focusedNodeId}
          onFocusNode={(nodeId) => setFocusedNodeId(nodeId)}
          onClearFocus={handleClearFocus}
          onCreateBlank={handleCreateBlank}
          onFocusedArtifactChange={setFocusedArtifactData}
          supabase={supabase}
          userRecord={userRecord}
          onNodeClick={handleNodeClick}
          onDeleteNode={deleteNode}
          onFilesDrop={(files) => {
            // Phase 2: file drop handler stub. Phase 3+ will wire this
            // to the chat panel's upload flow via a ref or shared hook.
            console.log('[workstation] files dropped:', files.map(f => f.name));
          }}
        />
        {mapExpanded && <ResizeHandle onMouseDown={startResize('canvas-map')} />}
        <WorkstationJourneyMap
          nodes={nodes}
          loading={nodesLoading}
          expanded={mapExpanded}
          thinking={thinking}
          focusedNodeId={focusedNodeId}
          onToggle={() => setMapExpanded(e => !e)}
          onNodeClick={handleNodeClick}
          onDeleteNode={deleteNode}
          supabase={supabase}
          workspaceId={workspaceId}
        />

        {/* ── EXCAVATE (Progress + Notes, full-screen 50/50) ── */}
        <ProjectExcavate
          workspaceId={workspaceId}
          userId={userId}
          visible={layer2Visible}
          onClose={() => setLayer2Visible(false)}
        />
      </div>

      {/* ── FOOTER (activity meter) ──────────────────── */}
      <WorkstationFooter nodes={nodes} workspaceCreatedAt={workspaceSession?.created_at} />

      {/* ── SHORTCUT HELP MODAL ──────────────────────── */}
      {shortcutHelpOpen && <ShortcutHelp onClose={() => setShortcutHelpOpen(false)} />}
    </div>
  );
}

// ── Shortcut help modal ────────────────────────────────────
// Cheat sheet for the workstation keyboard shortcuts. Opens via ⌘/
// from anywhere (including while typing). Click outside or Esc closes.
function ShortcutHelp({ onClose }) {
  const isMac = typeof navigator !== 'undefined' && /Mac/.test(navigator.platform);
  const mod = isMac ? '⌘' : 'Ctrl';
  const rows = [
    [`${mod} 1`, 'Focus chat input'],
    [`${mod} 2`, 'Focus canvas'],
    [`${mod} 3`, 'Toggle journey map'],
    [`${mod} E`, 'Toggle Excavate'],
    [`${mod} [`, 'Previous artifact'],
    [`${mod} ]`, 'Next artifact'],
    [`${mod} /`, 'Show / hide this list'],
    ['↑ ↓ ← →', 'Navigate canvas tiles'],
    ['Enter', 'Open selected tile'],
    ['Esc', 'Back to gallery / close overlay'],
  ];
  return (
    <div
      onClick={onClose}
      style={{
        position: 'absolute',
        inset: 0,
        background: 'rgba(0,0,0,0.72)',
        zIndex: 200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#07070a',
          border: '1px solid #272736',
          borderRadius: 10,
          padding: '20px 26px',
          minWidth: 280,
          boxShadow: '0 8px 32px rgba(0,0,0,0.7)',
        }}
      >
        <div style={{
          fontSize: 10,
          color: '#666',
          fontFamily: 'Georgia, serif',
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          marginBottom: 14,
        }}>
          Keyboard Shortcuts
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {rows.map(([key, label]) => (
            <div key={key} style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 24,
            }}>
              <span style={{
                fontSize: 12,
                color: '#d4d4d4',
                fontFamily: 'Georgia, serif',
              }}>
                {label}
              </span>
              <span style={{
                fontSize: 11,
                color: '#c8a96e',
                fontFamily: 'Courier New, monospace',
                background: '#111118',
                border: '1px solid #2a2a2a',
                borderRadius: 4,
                padding: '2px 8px',
                whiteSpace: 'nowrap',
              }}>
                {key}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Resize handle ──────────────────────────────────────────
// 4px-wide vertical column in the workstation grid. Hover lights up
// the centerline in sage so the user can see it as a grabbable target.
// The actual drag math lives in workstation.js (startResize).
function ResizeHandle({ onMouseDown }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onMouseDown={onMouseDown}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width: 4,
        cursor: 'col-resize',
        background: hovered ? 'rgba(122,158,130,0.25)' : 'transparent',
        borderLeft: '1px solid #1e1e2a',
        borderRight: '1px solid #1e1e2a',
        position: 'relative',
        transition: 'background 0.12s',
      }}
    >
      <div style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 2,
        height: 28,
        borderRadius: 1,
        background: hovered ? '#7a9e82' : '#2a2a2a',
        transition: 'background 0.12s',
      }} />
    </div>
  );
}
